import { CURRENCY } from 'services/interfaces';
import routes from './routes.internal.json';
import { FORM_FIELDS } from './form.constants';

export * from './form.constants';
export * from './formbuilder.constants';
export * from './regexp.constants';
export * from './profile.constants';

type PageName =
    | 'LANDING'
    | 'CARDS_LANDING'
    | 'CARDS_WHITELABEL'
    | 'CARDS_RESTORE'
    | 'SCORING'
    | 'RESTORE'
    | 'CALLCENTER'
    | 'CALLCENTER_MAIN_FORM_EDIT'
    | 'CALLCENTER_FULL_PATH'
    | 'WHITELABEL'
    | 'AGENTS'
    | 'WAYBACK'
    | 'AVITO'
    | 'DEALS'
    | 'PROMO_SKB'
    | 'PROMO_RAIFFAIZEN'
    | 'PROMO_WEBBANKIR'
    | 'PROMO_ZAIMER'
    | 'PROMO_MTS'
    | 'MAIN_FORM_EDIT'
    | 'SEND'
    | 'PROMO_REFINANCING'
    | 'THANKS_FOR_INTEREST'
    | 'CREDIT_CARDS_LANDING'
    | 'CRM_CREDIT_CARDS_LANDING'
    | 'CRM_AUTO_SEND';

export const PAGE: Record<PageName, keyof typeof routes> = {
    LANDING: 'landing',
    CARDS_LANDING: 'cards',
    SCORING: 'scoring',
    RESTORE: 'restore',
    CARDS_RESTORE: 'cardsRestore',
    CALLCENTER: 'callcenter',
    CALLCENTER_MAIN_FORM_EDIT: 'callcenterMainFormEdit',
    CALLCENTER_FULL_PATH: 'callcenterFullPath',
    WHITELABEL: 'whitelable',
    CARDS_WHITELABEL: 'cardsWhitelable',
    AGENTS: 'agents',
    WAYBACK: 'wayback',
    AVITO: 'avito',
    DEALS: 'deals',
    PROMO_SKB: 'promoSkb',
    PROMO_RAIFFAIZEN: 'promoRaiffeisen',
    PROMO_WEBBANKIR: 'promoWebbankir',
    PROMO_ZAIMER: 'promoZaimer',
    PROMO_MTS: 'promoMts',
    MAIN_FORM_EDIT: 'mainFormEdit',
    SEND: 'send',
    PROMO_REFINANCING: 'promoRefinancing',
    THANKS_FOR_INTEREST: 'thanksForInterest',
    CREDIT_CARDS_LANDING: 'creditCardsLanding',
    CRM_CREDIT_CARDS_LANDING: 'crmCreditCardsLanding',
    CRM_AUTO_SEND: 'crmAutoSend',
};

export const BANK_DECISION_TYPES = {
    WITH_INSURANCE: 'REQUESTED_VALUE_WITH_INSURANCE',
    WITHOUT_INSURANCE: 'REQUESTED_VALUE_WITHOUT_INSURANCE',
    MAXSUM_WITH_INSURANCE: 'MAXSUM_WITH_INSURANCE',
    MAXSUM_WITHOUT_INSURANCE: 'MAXSUM_WITHOUT_INSURANCE',
};

export const FORM_NAME = 'CreditOnlineV3';

export const AMOUNT_LIMITS = {
    MAX: 15e6,
    MIN: 1000,
    STEP: 10000,
};

export const INITIAL_FEE_LIMITS = {
    MAX: 15e6,
    MIN: 0,
};

export const STATUS_COUNTDOWN = {
    LONG: 120,
    SHORT: 30,
};

export const FOOTER_RATES = {
    min: 9,
    max: 40,
};

export const WHITELABEL_QUERY_PARAMS_MAP = {
    [FORM_FIELDS.PURPOSE]: 'purpose',
    [FORM_FIELDS.AMOUNT]: 'desired_credit_amount',
    [FORM_FIELDS.PERIOD]: 'desired_credit_term_years',
    [FORM_FIELDS.PHONE]: 'phone_mobile',
    [FORM_FIELDS.EMAIL]: 'email',
    [FORM_FIELDS.INITIAL_FEE]: 'down_payment',
    [FORM_FIELDS.FIRST_NAME]: 'name',
    [FORM_FIELDS.LAST_NAME]: 'surname',
    [FORM_FIELDS.MIDDLE_NAME]: 'patronymic',
    [FORM_FIELDS.GENDER]: 'gender',
    [FORM_FIELDS.MONTHLY_SALARY]: 'monthly_salary',
    [FORM_FIELDS.PARTNER_ID]: 'aff_id',
} as const;

export const WAYBACK_FIELDS_MAP = {
    personalDataConsent: FORM_FIELDS.AGREEMENT,
    amount: FORM_FIELDS.AMOUNT,
    initialFee: FORM_FIELDS.INITIAL_FEE,
    term: FORM_FIELDS.PERIOD,
    purpose: FORM_FIELDS.PURPOSE,
    solvencyProof: FORM_FIELDS.INCOME_CONFIRMATION,
    firstName: FORM_FIELDS.FIRST_NAME,
    middleName: FORM_FIELDS.MIDDLE_NAME,
    lastName: FORM_FIELDS.LAST_NAME,
    email: FORM_FIELDS.EMAIL,
    phone: FORM_FIELDS.PHONE,
    additionalPhone: FORM_FIELDS.ADDITIONAL_PHONE,
    additionalPhoneOwner: FORM_FIELDS.ADDITIONAL_PHONE_OWNER,
    additionalPhoneOwnerFio: FORM_FIELDS.ADDITIONAL_PHONE_OWNER_FIO,
    birthDate: FORM_FIELDS.BIRTHDAY,
    birthPlace: FORM_FIELDS.BIRTH_PLACE,
    passportNumber: FORM_FIELDS.PASSPORT_NUMBER,
    passportIssueDate: FORM_FIELDS.PASSPORT_DATE,
    passportIssuedBy: FORM_FIELDS.ISSUED,
    passportIssuedByCode: FORM_FIELDS.UNIT_CODE,
    registrationDate: FORM_FIELDS.REGISTRATION_DATE,
    maritalStatus: FORM_FIELDS.FAMILY_STATUS,
    education: FORM_FIELDS.EDUCATION,
    snils: FORM_FIELDS.SNILS,
    salaryBank: FORM_FIELDS.SALARY_BANK,
    employmentType: FORM_FIELDS.EMPLOYMENT_TYPE,
    organizationName: FORM_FIELDS.ORGANIZATION_NAME,
    organizationInn: FORM_FIELDS.EMPLOYER_INN,
    organizationType: FORM_FIELDS.EMPLOYER_TYPE,
    organizationActivity: FORM_FIELDS.AREA_OF_EMPLOYMENT,
    lastExperienceStartDate: FORM_FIELDS.LAST_EXPERIENCE,
    jobTitle: FORM_FIELDS.JOB_TITLE,
    jobType: FORM_FIELDS.JOB_TYPE,
    monthlyIncome: FORM_FIELDS.MONTHLY_SALARY,
    unemploymentReason: FORM_FIELDS.UNEMPLOYMENT_REASON,
    seniority: FORM_FIELDS.SENIORITY,
    estateType: FORM_FIELDS.PROPERTY_TYPE,
    carType: FORM_FIELDS.CAR,
    dependentsCount: FORM_FIELDS.NUMBER_OF_DEPENDENTS,
    flatRentAmount: FORM_FIELDS.RENT_APARTMENT,
    additionalIncome: FORM_FIELDS.ADDITIONAL_INCOME,
};

export const FB_FIELDS_EXTRA: Record<string, keyof FB.Request> = {
    HOME_ADDRESS: 'home_address',
    REGISTRATION_ADDRESS: 'registration_address',
};

export enum FB_SMS_STATUS {
    CHECKING_CODE = 1,
    WAITING_CODE,
    CORRECT_CODE,
    WRONG_CODE,
}

export const PRODUCTS_ID = {
    AUTO2CREDIT: '5f3bc40cae791b001fe8960e',
    CREDIT_DOCTOR: '618d12cbeb9a29001f454bad',
    PROMO_SKB: '6024f11999f157001f1b7312',
    PROMO_RAIFFAIZEN: '5e5782ac1861f6001122e17b',
    PROMO_WEBBANKIR: '5e95c9aa31b105001bb766b8',
    PROMO_ZAIMER: '5e95e240b92fe7001be7598c',
    RAIFFEISEN_REFINANCE: '5e5782ea1861f6001122fbdb',
    RAIFFEISEN_CASH_CREDIT: '5e5782ac1861f6001122e17b',
    TINKOFF_CREDIT_CARD: 'fa89115a-b3f2-4286-a9c3-6c7d45d1aeba--72edfbe3-9fef-435f-a62a-8fe25c00441e',
    TINKOFF_PLATINUM_CREDIT_CARD: 'ec395ef7-4911-4da6-bc47-140b443d4d19--72edfbe3-9fef-435f-a62a-8fe25c00441e',
    CITI_BANK_CREDIT_CARD_SELECT: 'df810eed-86e9-428e-912b-8e377659d1a2--72edfbe3-9fef-435f-a62a-8fe25c00441e',
    ALFA_CARD_GOD_BEZ_PROCENTOV: 'f5c7865d-5a0e-4679-a965-bf90debe6764--bae435f2-eaa7-4e60-b121-85e4e18197c8',
    MTS_CARD_CASHBACK: '498625df-dd09-4c53-8fa8-ca07cc2f4299',
    MTS_CARD_WEEKEND: 'd84f9f39-c5db-41ef-acc5-7c7f64812e6b',
    MTS_NALICHNIMI: '5ea154fc0973a2001b13692a',
    OTKRITIYE_120_DAYS: '933b18e8-a75b-437e-bd73-9d3643fa2d6f',
    ATB_CASH_CREDIT: '61387808f1b8fb001f0e7599',
    MOBILE_WEBBANKIR: '62f241a980299b0023b64dcb',
    MKB_PROFITABLE_SEASON: '63c7bd80fb2e830026f92f5c',
    SKB_COMPLETELY_ONLINE: '622f4a001e7f230020a23d00',
    URALSIB_CARD_120_DAYS_MAXIMUM: '5f65c606-413b-4d0e-9424-02c15b30535c--133df7f2-b676-40d3-8aaa-f575dcf6b1f6',
    TINKOFF_CARD_PLATINUM_PROMO_PROD: 'fa89115a-b3f2-4286-a9c3-6c7d45d1aeba--133df7f2-b676-40d3-8aaa-f575dcf6b1f6',
    TINKOFF_CARD_PLATINUM_PROD_1: 'fa89115a-b3f2-4286-a9c3-6c7d45d1aeba',
    TINKOFF_CARD_PLATINUM_PROD_2: '73d0914d-de21-4b99-89d2-2a4a3c2dbc3b',
    TINKOFF_CARD_PLATINUM_PROD_OLD: '169',
    TINKOFF_CARD_PLATINUM_PROMO_DEV: '7414fab1-1e7f-42e3-ad6f-c2ba4e994f44',
    TINKOFF_CARD_PLATINUM_PROMO_DEV_2: '73882e08-0dc2-499c-b7d4-f5c5f1a6c7aa',
    TINKOFF_CARD_PLATINUM_PROMO_DEV_OLD: '169',
    TINKOFF_NALICHNIMI: '5e57828f1861f6001122d4b5',
    MTS_CARD_CASHBACK_PROMO: '498625df-dd09-4c53-8fa8-ca07cc2f4299--133df7f2-b676-40d3-8aaa-f575dcf6b1f6',
    SVOY_BANK_SVOYA_KREDITKA: 'ef69d26e-639f-4100-9ecc-2e1338084c5f--133df7f2-b676-40d3-8aaa-f575dcf6b1f6',
};

export const TABS_KEYS = {
    ONLINE: 'online',
    FINAL: 'final',
    SUITABLE: 'suitable',
    REFERRAL: 'referral',
} as const;

export const GROUPS_KEYS = {
    ONLINE: 'online',
    FINAL: 'final',
    SUITABLE: 'suitable',
    SUITABLE_MFO: 'suitableMfo',
    NEAREST_CITIES_MFO: 'nearestCitiesMfo',
    UNSUITABLE: 'unsuitable',
    RECOMMENDED_CREDITS: 'recommendedCredits',
    RECOMMENDED: 'recommended',
    REFERRAL: 'referral',
    NEAREST_CITIES: 'nearestCities',
    SUPER_RELEVANT: 'superRelevant',
    HAS_EXACT_CONDITIONS: 'hasExactConditions',
    ACTIVE: 'active',
    PROMO_BANNER: 'promoBanner',
    FEW_BANKS_BANNER: 'fewBanksBanner',
    EXACT_CONDITIONS: 'exactConditions',
} as const;

export const TABS = {
    [TABS_KEYS.ONLINE]: 'Точные условия',
    [TABS_KEYS.FINAL]: 'Финальная ставка',
    [TABS_KEYS.SUITABLE]: 'Другие кредиты',
    [TABS_KEYS.REFERRAL]: 'Реферальные',
} as const;

export const TABS_SHORT = {
    [TABS_KEYS.ONLINE]: 'Точные условия',
    [TABS_KEYS.FINAL]: 'Финальная ставка',
    [TABS_KEYS.SUITABLE]: 'Другие кредиты',
    [TABS_KEYS.REFERRAL]: 'Реферальные',
} as const;

export type TabsType = typeof TABS_KEYS[keyof typeof TABS_KEYS];
export type GroupType = typeof GROUPS_KEYS[keyof typeof GROUPS_KEYS];

export type GroupTreeType = {
    [key in TabsType]: GroupType[];
};

export const GROUP_TREE: GroupTreeType = {
    [TABS_KEYS.ONLINE]: [GROUPS_KEYS.ONLINE],
    // [TABS_KEYS.FINAL]: [GROUPS_KEYS.SUPER_RELEVANT, GROUPS_KEYS.FINAL],
    [TABS_KEYS.FINAL]: [],
    [TABS_KEYS.SUITABLE]: [
        GROUPS_KEYS.PROMO_BANNER,
        GROUPS_KEYS.EXACT_CONDITIONS,
        GROUPS_KEYS.FEW_BANKS_BANNER,
        GROUPS_KEYS.ACTIVE,
        GROUPS_KEYS.SUPER_RELEVANT,
        GROUPS_KEYS.SUITABLE,
        GROUPS_KEYS.SUITABLE_MFO,
        GROUPS_KEYS.NEAREST_CITIES_MFO,
        GROUPS_KEYS.RECOMMENDED_CREDITS,
        GROUPS_KEYS.RECOMMENDED,
        GROUPS_KEYS.NEAREST_CITIES,
    ],
    [TABS_KEYS.REFERRAL]: [GROUPS_KEYS.REFERRAL],
};

export const SUITABLE_GROUPS_FOR_EXACT_CONDITIONS = [
    GROUPS_KEYS.PROMO_BANNER,
    GROUPS_KEYS.EXACT_CONDITIONS,
    GROUPS_KEYS.FEW_BANKS_BANNER,
    GROUPS_KEYS.ACTIVE,
    GROUPS_KEYS.SUITABLE,
    GROUPS_KEYS.SUPER_RELEVANT,
    GROUPS_KEYS.SUITABLE_MFO,
    GROUPS_KEYS.NEAREST_CITIES_MFO,
    GROUPS_KEYS.RECOMMENDED_CREDITS,
    GROUPS_KEYS.RECOMMENDED,
    GROUPS_KEYS.NEAREST_CITIES,
];

export type GroupsTexts = {
    [key in GroupType]: {
        title: string;
        text: string;
        errors?: {
            title: string;
            text: string;
        };
    };
};

export const GROUPS_TEXTS: Partial<GroupsTexts> = {
    [GROUPS_KEYS.ONLINE]: {
        title: 'Кредит онлайн',
        text: 'В этих банках можно узнать реальные условия и получить кредит без визита в отделение',
    },
    [GROUPS_KEYS.FINAL]: {
        title: 'Предложения с финальной ставкой',
        text: 'Условия не изменятся, если с вашими документами всё в порядке',
    },
    [GROUPS_KEYS.SUITABLE]: {
        title: 'Ваши персональные предложения',
        text: 'Узнайте решение онлайн на Сравни и получите кредит в отделении банка',
    },
    [GROUPS_KEYS.REFERRAL]: {
        title: 'Реферальные предложения',
        text: 'Для оформления нужно перейти на сайт партнёра',
    },
    [GROUPS_KEYS.NEAREST_CITIES]: {
        title: 'Предложения в городах рядом с вами',
        text: 'В вашем городе больше нет предложений, но мы нашли их в городах поблизости',
    },
    [GROUPS_KEYS.RECOMMENDED]: {
        title: 'Возможно, вам подойдёт',
        text: 'Мы подобрали для вас кредиты на сумму и срок, максимально близкие к вашему запросу',
    },
    [GROUPS_KEYS.RECOMMENDED_CREDITS]: {
        title: 'Возможно, вам подойдёт',
        text: '',
    },
    [GROUPS_KEYS.NEAREST_CITIES_MFO]: {
        title: 'Деньги сразу на карту',
        text: 'Мы подобрали для вас онлайн займы на карту любого банка',
    },
    [GROUPS_KEYS.ACTIVE]: {
        title: 'Мои заявки',
        text: '',
    },
};

export const GROUPS_TEXT_WITH_MFO: Partial<GroupsTexts> = {
    [GROUPS_KEYS.RECOMMENDED]: {
        title: 'Возможно, вам подойдет',
        text: 'Мы подобрали для вас предложения на сумму и срок, максимально близкие к вашему запросу',
    },
};

export const CURRENCIES: Record<CURRENCY, { SYMBOL: string; SYMBOL_ALTERNATIVE: string }> = {
    RUB: {
        SYMBOL: 'Р',
        SYMBOL_ALTERNATIVE: '₽',
    },
    USD: {
        SYMBOL: '$',
        SYMBOL_ALTERNATIVE: '$',
    },
    EUR: {
        SYMBOL: '',
        SYMBOL_ALTERNATIVE: '',
    },
};

export const MONTHS = [
    'Январь',
    'Февраль',
    'Март',
    'Апрель',
    'Май',
    'Июнь',
    'Июль',
    'Август',
    'Сентябрь',
    'Октябрь',
    'Ноябрь',
    'Декабрь',
];

export const MONTHS_GENITIVE = [
    'Января',
    'Февраля',
    'Марта',
    'Апреля',
    'Мая',
    'Июня',
    'Июля',
    'Августа',
    'Сентября',
    'Октября',
    'Ноября',
    'Декабря',
];

/** @deprecated */
export const ADDRESS_FIAS_LEVEL = ['4', '5', '6', '7', '65'];

/**
 * @url https://support.dadata.ru/knowledge-bases/4/articles/1059-urovni-fias-i-urovni-adresa-dadatyi
 * 8 - дом, 65 - планировочная структура, 75 - участок, хозяйство
 */
export const ADDRESS_STREET_HOUSE_FIAS_LEVEL = ['8', '65', '75'];

export const HTTP_ERROR_IMAGES: { [index: number]: string } = {
    404: 'https://www.sravni.ru/f/images/404.png',
    500: 'https://www.sravni.ru/f/images/500.png',
};

export const ASPX_ANONYMOUS_KEY = '.ASPXANONYMOUS';

const AUTO_NUMBER_LETTERS = 'авекмнорстух';

export const MASKS = {
    PHONE: [
        '+',
        '7',
        ' ',
        '(',
        /[1-9]|\*/,
        /\d|\*/,
        /\d|\*/,
        ')',
        ' ',
        /\d|\*/,
        /\d|\*/,
        /\d|\*/,
        '-',
        /\d|\*/,
        /\d|\*/,
        '-',
        /\d|\*/,
        /\d|\*/,
    ],
    DATE: [/\d/, /\d/, '.', /\d/, /\d/, '.', /[1-2]/, /\d/, /\d/, /\d/],
    MONTH: [/\d/, /\d/, '.', /[1-2]/, /\d/, /\d/, /\d/],
    PASSPORT: [/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
    UNIT_CODE: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/],
    FOUR_DIGITS: [/\d/, /\d/, /\d/, /\d/],
    SNILS: [/\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, '-', /\d/, /\d/],
    AUTO_NUMBER: [
        new RegExp(`[${AUTO_NUMBER_LETTERS}]`, 'i'),
        ' ',
        /\d/,
        /\d/,
        /\d/,
        ' ',
        new RegExp(`[${AUTO_NUMBER_LETTERS}]`, 'i'),
        new RegExp(`[${AUTO_NUMBER_LETTERS}]`, 'i'),
        ' ',
        /\d/,
        /\d/,
        /\d?/,
    ],
};

export const PLACEHOLDERS = {
    PHONE: '+7 (___) ___-__-__',
    DATE: '__.__.____',
    MONTH: '__.____',
    PASSPORT: '____ ______',
    UNIT_CODE: '___-___',
    SNILS: '___-___-___-__',
    AUTO_NUMBER: '_ ___ __ ___',
};

export const AUTO_NUMBER_REG_EXP = new RegExp(`[${AUTO_NUMBER_LETTERS}]\\d{3}[${AUTO_NUMBER_LETTERS}]{2}\\d{2,3}`, 'i');

export const FORM_STEPS = {
    PARAMS: 'params',
    PASSPORT: 'passport',
    WORK: 'work',
    ADDITIONAL: 'additional',
    PERSONAL_DATA: 'personalData',
};

export const EN_RU_BINDING: { [key: string]: string } = {
    q: 'й',
    w: 'ц',
    e: 'у',
    r: 'к',
    t: 'е',
    y: 'н',
    u: 'г',
    i: 'ш',
    o: 'щ',
    p: 'з',
    a: 'ф',
    s: 'ы',
    d: 'в',
    f: 'а',
    g: 'п',
    h: 'р',
    j: 'о',
    k: 'л',
    l: 'д',
    z: 'я',
    x: 'ч',
    c: 'с',
    v: 'м',
    b: 'и',
    n: 'т',
    m: 'ь',
    '[': 'х',
    '{': 'Х',
    ']': 'ъ',
    '}': 'Ъ',
    '`': 'ё',
    '~': 'Ё',
    ';': 'ж',
    ':': 'Ж',
    // '\'': 'э',
    // ',': 'б',
    '<': 'Б',
    // '.': 'ю',
    '>': 'Ю',
    // '|': '/',
    // '"': 'Э',
    // '/': '.',
    // '?': ',',
    // '@': '"',
    // '#': '№',
    // $: ';',
    // '^': ':',
    // '&': '?'
};

export const RU_EN_BINDING = Object.entries(EN_RU_BINDING).reduce<typeof EN_RU_BINDING>((acc, [key, value]) => {
    acc[value] = key;
    return acc;
}, {});

export const PARTNER_COOKIE_KEY = 'prc_creditSelection';

export const AFF_ID_COOKIE_KEY = 'aff_id';

export const INT_ADS_COOKIE_KEY = 'int_ads';

export const INT_CROSS_KEY = 'int_cross';

export const UTMZ_COOKIE_KEY = '__utmz';

export const LOCATION_COOKIE_KEY = '_SL_';
export const IP_LOCATION_COOKIE_KEY = '_ipl';
export const CS_LOCATION_COOKIE_KEY = '_cs_route';

export const UTM_MARKS = {
    SOURCE: 'utm_source',
    CAMPAIGN: 'utm_campaign',
    MEDIUM: 'utm_medium',
    TERM: 'utm_term',
    CONTENT: 'utm_content',
} as const;

export const INT_ADS_FB_MAP = {
    int_source: 'PromotionSource',
    int_medium: 'PromotionCategory',
    int_campaign: 'PromotionCampaign',
    int_content: 'PromotionSub1',
    int_term: 'PromotionSub2',
};

export const INT_MEDIUM_VALUES = {
    ADS: 'ads',
    CROSS: 'cross',
};

export const UTM_FB_MAP = {
    utm_source: 'UtmSource',
    utm_medium: 'UtmMedium',
    utm_campaign: 'UtmCampaign',
    utm_term: 'UtmTerm',
    utm_content: 'UtmContent',
};

export const UTM_COOKIE_MAP = {
    [UTM_MARKS.SOURCE]: 'utmcsr',
    [UTM_MARKS.CAMPAIGN]: 'utmccn',
    [UTM_MARKS.MEDIUM]: 'utmcmd',
    [UTM_MARKS.TERM]: 'utmctr',
    [UTM_MARKS.CONTENT]: 'utmcct',
} as const;

export const PARTNER_KEY = 'aff_id';

export const UTM_KEYS = {
    MEDIUM: 'utm_medium',
    SOURCE: 'utm_source',
    CAMPAIGN: 'utm_campaign',
    CONTENT: 'utm_content',
};

export const UTM_VALUES = {
    MEDIUM: ['email', 'sms'],
    SOURCE: 'trigger',
    CAMPAIGN: 'creditselection_megafon',
    CONTENT: '--segment_osago',
};
export const PARTNER_ID = 'aff_id';

export const COOKIE_PARTNER_KEYS = [
    PARTNER_KEY,
    'p_offer_id',
    'source',
    'aff_sub',
    'aff_sub2',
    'aff_sub3',
    'aff_sub4',
    'aff_sub5',
    'tid',
];

export const COOKIE_PARTNER_MAP_TO_FB = {
    p_offer_id: 'aff_offer_id',
    source: 'aff_source',
    tid: 'aff_tid',
};

export const QUERY_PARTNER_KEYS = [
    PARTNER_KEY,
    'offer_id',
    'source',
    'aff_sub',
    'aff_sub2',
    'aff_sub3',
    'aff_sub4',
    'aff_sub5',
    'transaction_id',
    'agentid',
];

export const QUERY_PARTNER_MAP_TO_FB = {
    transaction_id: 'aff_tid',
    offer_id: 'aff_offer_id',
    source: 'aff_source',
};

export const FINSPIN_ID = '5e62272bf535d1001cd9e1dc';
export const CITIBANK_ID = '266';
// export const POCHTABANK_ID = '72';
export const MTS_ID = '278';
export const OTKRITIYE_BANK_ID = '361';
export const ROSSSELHOZ_BANK_ID = process.env.ENV === 'production' ? '64f6e21d1da838002762d50c' : '254';
export const HOME_CREDIT_BANK_ID = '673';
export const SOVCOMBANK_ID = '170';
export const RAIFFEISEN_BANK_ID = '330';
export const ALFA_BANK_ID = '326';
export const MIGCREDIT_ID = '143002';
export const ZENIT_BANK_ID = '285';
export const TINKOFF_BANK_ID = '645';
export const ATB_ID = '57';
export const PROMSVIAZBANK_ID = '553';
export const SKB_BANK_ID = '902';
export const SBER_ID = '276';

export const URALSIB_BANK_ID = '5f65c606-413b-4d0e-9424-02c15b30535c--133df7f2-b676-40d3-8aaa-f575dcf6b1f6';

export const DISABLE_BANK_OFFERS_TIME_IN_DAYS: {
    DEFAULT: number;
    [key: string]: number;
} = {
    DEFAULT: 30,
    [SOVCOMBANK_ID]: 60,
} as const;

// Список банков, что принимают предзаявку по кредиту
export const ORGANIZATIONS_WITH_PRELIMINARYREQUEST: string[] = [
    // TODO: LOAN-1558 LOAN-1334
    // HOME_CREDIT_BANK_ID
];

export const IGNORE_SCORING_BY_TYPE = 'ignore_scoring_by_type';
export const IGNORE_SCORING_BY_IDS = 'ignore_scoring_by_ids';

export const REVIEWS_PAGE_SIZE = 10;
export const MIN_REVIEWS_COUNT = 3;

export const ESIA_KEYS = {
    FIRST_NAME: 'esia:firstname',
    MIDDLE_NAME: 'esia:middlename',
    LAST_NAME: 'esia:lastname',
    TRUSTED: 'esia:trusted',
} as const;

export const ESIA_TRUSTED_VALUES = {
    TRUE: 'True',
    FALSE: 'False',
};

export const SUBMIT_REQUEST_EVENT = 'SUBMIT_REQUEST_EVENT';

export const MARKETING_CAMPAIGNS = {
    LOAN_AS_GIFT: {
        beginDate: new Date('2021-10-15'),
        endDate: new Date('2021-11-15'),
        resultDate: new Date('2021-11-30'),
        endDateLoanAccepted: new Date('2021-11-29'),
    },
    REPAY_LOAN: {
        sendRequest: {
            fromDate: new Date('2021-11-25'),
            toDate: new Date('2021-12-25'),
        },
        getLoan: {
            fromDate: new Date('2021-11-25'),
            toDate: new Date('2021-12-31'),
        },
    },
};

export enum ClientAvailabilityStatusType {
    NEED_SIGN_CONSENT_DOCUMENTS = 'NEED_SIGN_CONSENT_DOCUMENTS',
    SUCCESS = 'SUCCESS',
    UNKNOWN = 'UNKNOWN',
}

export const IFRAME_RESIZER_SCROLL_EVENT = 'IFRAME_SCROLLED';

export const IFRAME_RESIZER_MESSAGES = {
    STOP_AUTO_RESIZING: 'STOP_AUTO_RESIZING',
    UPDATE_STATIC_HEIGHT: 'UPDATE_STATIC_HEIGHT',
};

export const ORGANIZATION_CHOICE_TYPE = {
    SUGGEST: 'suggest',
    BY_YOURSELF: 'byYourself',
    AUTO: 'auto',
} as const;

export type OrganizationChoiceType = ValueOf<typeof ORGANIZATION_CHOICE_TYPE>;

export type ValidationValues = {
    amountMin: number;
    amountMax: number;
};

export const ORGANIZATION_CHOICE_TYPE_EVENT_LABELS: Record<OrganizationChoiceType, string> = {
    [ORGANIZATION_CHOICE_TYPE.SUGGEST]: 'Саджест',
    [ORGANIZATION_CHOICE_TYPE.BY_YOURSELF]: 'Заполнил самостоятельно',
    [ORGANIZATION_CHOICE_TYPE.AUTO]: 'Проброс',
};

export const MPL_CLIENT_SCREEN_STATE_VALUES = {
    INITIAL_CLIENT_LOADING: 'initial_client_loading',
    INITIAL_REGISTER_LOADING: 'initial_register_loading',
    NEW_CLIENT: 'new_client',
    BLOCKED: 'blocked',
    REPEAT_CLIENT: 'repeat_client',
    ACTIVE_DEAL: 'active_deal',
    RE_SIGN: 're_sign',
    SLIDERS: 'sliders',
    REFUSED: 'refused',
    SUCCESSFULLY_CLOSED: 'successfully_closed',
    NOT_ACTIVE: 'not_active',
    DENIED_ESIA: 'denied_esia',
    LOGIN_REQUIRED: 'login_required',
    MPL_DISABLED: 'mpl_disabled',
    PENDING: 'pending',
    EXPIRED: 'expired',
} as const;

export type MplClientScreenStateType = ValueOf<typeof MPL_CLIENT_SCREEN_STATE_VALUES>;

export const LOCAL_STORAGE_KEY = 'paramsStep';
export const LOCAL_FORM = 'localForm';

export const LOCAL_STORAGE_WL_KEY = 'wlFormData';

export const SESSION_STORAGE_FORM_KEY = 'sessionFormData';

export const LOCAL_STORAGE_DEV_AB_EXPERIMENTS = 'localDevExperiments';

export const CREDIT_SELECTION_ABC_EXPERIMENT_IDS = {
    NEW_FOCUSED_FORM: 'b0a303d4-eec4-4e05',
    PROMOTION_GROUP_ID: '5a827bad-4454-4758',
    DETAILS_BUTTON_TEXT_ID: 'a182b343-7e10-4ba4',
    OFFERS_RANKING: '14dd92b7-2ff9-4253',
    PROMO_TINKOFF_BANNER: '6885c7ab-641e-4f57',
    SUITABLE_OFFERS_BANNER: 'd0fe6e72-5865-49d3',
    ADDITIONAL_QUESTIONS: 'dac1607d-dce9-4dcb',
    EXACT_CONDITIONS: '4ce819b4-ac29-4ce6',
    OPTION_PATRONYMIC: 'e7fcf61e-c216-4405',
} as const;

export const CONSENT_TO_PROCESSING_PERSONAL_DATA_URL =
    'https://www.sravni.ru/promo/soglasie-na-obrabotku-personalnyh-dannyh/';
export const CONSENT_TO_ADVERTISING = 'https://f.sravni.ru/cms/file/FilePath/soglasie-na-poluchenie-reklamy.pdf';

export const SESSION_STORAGE_OSAGO_POPUP_KEY = 'isShowedOsagoPopup';

export const OFFERS_QUANTITY_PROMOTION_GROUP = 3;

export const TRACE_ID_HEADER = 'X-Trace-Id';
